import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link, Navigate } from 'react-router-dom';
import {
  deleteImage,
  removeBackgroundImage,
  getUserGallery,
  normalizePaginatedItems,
  GALLERY_PAGE_SIZE,
  readFlowSelectedProduct,
  getProductDetails,
  catalogProductPrintingProvider,
  productDetailsHeroImage,
  clearFlowSelectedProduct,
} from '../apiClient';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import SimpleImageCard from './SimpleImageCard';
import SelectableImageCard from './SelectableImageCard';
import ProductSelectionFlowBanner from './ProductSelectionFlowBanner';

const Gallery = () => {
  const location = useLocation();
  const [sessionFallback] = useState(() => readFlowSelectedProduct());
  const selectedProduct =
    location.state?.selectedProduct ?? sessionFallback?.selectedProduct ?? null;
  const navigate = useNavigate();
  const session = useSessionContext();

  const [images, setImages] = useState([]);
  const [galleryTotal, setGalleryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [productHeroImage, setProductHeroImage] = useState(() => {
    const p = location.state?.selectedProduct ?? sessionFallback?.selectedProduct;
    return p?.image || p?.thumbnail || sessionFallback?.heroImage || null;
  });

  useEffect(() => {
    if (!selectedProduct?.id) {
      setProductHeroImage(null);
      return;
    }
    const fromRow =
      selectedProduct.image || selectedProduct.thumbnail || sessionFallback?.heroImage || null;
    setProductHeroImage(fromRow);
    const printingProvider = catalogProductPrintingProvider(selectedProduct);
    let cancelled = false;
    (async () => {
      try {
        const details = await getProductDetails(selectedProduct.id, {
          printing_provider: printingProvider,
        });
        if (cancelled) return;
        const hero = productDetailsHeroImage(details) || fromRow;
        if (hero) setProductHeroImage(hero);
      } catch {
        if (!cancelled) setProductHeroImage(fromRow);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    selectedProduct?.id,
    selectedProduct?.image,
    selectedProduct?.thumbnail,
    selectedProduct?.catalog_provider,
    sessionFallback?.heroImage,
  ]);

  const handleImageSelect = (image) => {
    if (selectedProduct) {
      clearFlowSelectedProduct();
      navigate('/configure-mockup', { state: { selectedProduct: selectedProduct, selectedImage: image } });
    } else {
      navigate('/products', { state: { selectedImage: image } });
    }
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prevSelectedImages => {
      const newSelectedImages = new Set(prevSelectedImages);
      if (newSelectedImages.has(imageId)) {
        newSelectedImages.delete(imageId);
      } else {
        newSelectedImages.add(imageId);
      }
      return newSelectedImages;
    });
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedImages.size} images? This action cannot be undone.`)) {
      try {
        await Promise.all(Array.from(selectedImages).map(id => deleteImage(id)));
        setImages(images.filter((img) => !selectedImages.has(img.id)));
        setSelectedImages(new Set());
      } catch (err) {
        console.error('Error deleting images:', err);
        setError('Failed to delete images. Please try again later.');
      }
    }
  };

  const fetchGalleryImages = useCallback(async () => {
    try {
      setError(null);
      const data = await getUserGallery({ limit: GALLERY_PAGE_SIZE, offset: 0 });
      const rows = normalizePaginatedItems(data);
      const processedImages = rows.map((image) => ({ ...image, id: image.id }));
      setImages(processedImages);
      setGalleryTotal(typeof data?.total === 'number' ? data.total : processedImages.length);
    } catch (err) {
      console.error('Error fetching gallery images:', err);
      setError('Failed to load images. Please try again later.');
    }
  }, []);

  const loadMoreGallery = useCallback(async () => {
    if (loadingMore || images.length >= galleryTotal) return;
    try {
      setLoadingMore(true);
      const data = await getUserGallery({ limit: GALLERY_PAGE_SIZE, offset: images.length });
      const rows = normalizePaginatedItems(data);
      if (typeof data?.total === 'number') setGalleryTotal(data.total);
      setImages((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        const merged = [...prev];
        for (const image of rows) {
          const row = { ...image, id: image.id };
          if (row.id != null && !seen.has(row.id)) {
            seen.add(row.id);
            merged.push(row);
          }
        }
        return merged;
      });
    } catch (err) {
      console.error('Error loading more gallery images:', err);
      setError('Failed to load more images.');
    } finally {
      setLoadingMore(false);
    }
  }, [images.length, galleryTotal, loadingMore]);

  const handleBulkRemoveBackground = async () => {
    try {
      setProcessing(true);
      await Promise.all(
        Array.from(selectedImages).map((id) => removeBackgroundImage(id))
      );
      setSelectedImages(new Set());
      // Refetch gallery from server to get fresh image URLs (avoids caching/display issues)
      await fetchGalleryImages();
    } catch (err) {
      console.error('Error removing backgrounds:', err);
      setError('Failed to remove backgrounds. Please try again later.');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (session.loading || !session.doesSessionExist) {
      setLoading(false);
      return;
    }

    const loadGallery = async () => {
      setLoading(true);
      await fetchGalleryImages();
      setLoading(false);
    };
    loadGallery();
  }, [session.loading, session.doesSessionExist, fetchGalleryImages]);

  if (session.loading) {
    return null; // Or a spinner
  }

  if (!session.doesSessionExist) {
    if (selectedProduct) {
      return <Navigate to="/designs" replace state={{ selectedProduct }} />;
    }
    return (
      <div className="container mx-auto p-6 flex flex-col items-center justify-center min-h-[60vh] text-center tour-gallery">
        <div className="bg-white p-10 rounded-xl shadow-2xl max-w-lg w-full transform transition-all hover:scale-105 duration-300">
          <div className="mb-8">
            <svg className="w-24 h-24 mx-auto text-primary opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">Your Gallery Awaits</h1>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Your personal gallery is for saved AI designs. To buy without an account, pick a ready-made design.
          </p>
          <div className="flex flex-col gap-4">
            <Link
              to="/designs"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-1 block"
            >
              Browse Ready-Made Designs
            </Link>
            <Link
              to="/auth"
              className="w-full bg-white text-gray-700 font-semibold py-3 px-6 rounded-lg border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition duration-300 block"
            >
              Log In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 bg-card rounded-lg shadow-lg tour-gallery max-w-full overflow-x-hidden">
      {/* ... (Existing Authenticated UI) ... */}
      <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Your Image Gallery</h1>
      {selectedProduct ? (
        <ProductSelectionFlowBanner selectedProduct={selectedProduct} productImageUrl={productHeroImage} />
      ) : (
        <p className="text-center text-gray-600 mb-10">Select an image to choose a product to print it on.</p>
      )}

      {images.length > 0 && (
        <div className="flex justify-end mb-6">
          <button
            onClick={handleBulkRemoveBackground}
            disabled={selectedImages.size === 0 || processing}
            className="bg-primary hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md mr-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {processing ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : 'Remove Background'}
          </button>
          <button onClick={handleBulkDelete} disabled={selectedImages.size === 0 || processing} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
            Delete
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-600">Loading images...</p>
      ) : error ? (
        <p className="text-center text-red-500">{error}</p>
      ) : images.length === 0 ? (
        <p className="text-center text-gray-600">No images generated yet. Go to the main page to create some!</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {images.map((image) => (
            selectedProduct ? (
              <SelectableImageCard key={image.id} image={image} onSelect={handleImageSelect} onToggleSelection={toggleImageSelection} isSelected={selectedImages.has(image.id)} />
            ) : (
              <SimpleImageCard key={image.id} image={image} onClick={handleImageSelect} onToggleSelection={toggleImageSelection} isSelected={selectedImages.has(image.id)} />
            )
          ))}
        </div>
      )}
      {!loading && !error && images.length > 0 && images.length < galleryTotal && (
        <div className="flex justify-center mt-8">
          <button
            type="button"
            onClick={loadMoreGallery}
            disabled={loadingMore}
            className="px-6 py-3 rounded-lg bg-primary text-white font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Gallery;
